// Shared formatting + small badges for the admin Cost dashboard.

import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Badge } from "../../../ui/badge";

// Money. Sub-cent values (which matter at scale) keep 4 decimals; everything
// else renders as a conventional 2-decimal dollar figure with thousands
// separators. Pass `precise` to always show 4 decimals (e.g. $/min).
export const fmtUsd = (v, precise = false) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (precise || (n !== 0 && Math.abs(n) < 0.01)) {
    return `$${n.toLocaleString(undefined, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    })}`;
  }
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const fmtNum = (v) =>
  v == null ? "—" : Number(v).toLocaleString();

// Compact token counts (240K, 1.2M) — the full value goes in a title tooltip.
export const fmtTokens = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `${n}`;
};

// Friendly labels for the rate/token bucket keys (keys differ by channel).
export const TOKEN_LABELS = {
  input: "Input",
  cached_input: "Cached input",
  output: "Output",
  text_input: "Text in",
  cached_text_input: "Cached text in",
  audio_input: "Audio in",
  cached_audio_input: "Cached audio in",
  text_output: "Text out",
  audio_output: "Audio out",
};

const sumTokens = (tokens) =>
  Object.values(tokens || {}).reduce((s, v) => s + (Number(v) || 0), 0);

// Inline cached/input/output breakdown for one token object. Optional `label`
// renders a small header with the summed total.
export const TokenBreakdown = ({ tokens, label }) => {
  const entries = Object.entries(tokens || {}).filter(([, v]) => v != null);
  if (!entries.length) {
    return <span className="text-xs text-muted-foreground">no token data</span>;
  }
  return (
    <div>
      {label ? (
        <div className="mb-1 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
          <span className="font-normal normal-case text-muted-foreground/70">
            {fmtTokens(sumTokens(tokens))} total
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {entries.map(([k, v]) => (
          <span
            key={k}
            className="tabular-nums"
            title={`${Number(v).toLocaleString()} tokens`}
          >
            <span className="text-muted-foreground">{TOKEN_LABELS[k] || k}</span>{" "}
            {fmtTokens(v)}
          </span>
        ))}
      </div>
    </div>
  );
};

export const fmtMinutes = (v) => {
  if (v == null) return "—";
  return `${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}m`;
};

export const fmtDuration = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// "voice" / "chat" pill, colour-coded to match the dashboards.
export const ChannelBadge = ({ channel }) => (
  <Badge
    variant="outline"
    className={
      channel === "voice"
        ? "border-violet-300 bg-violet-50 text-violet-700"
        : "border-sky-300 bg-sky-50 text-sky-700"
    }
  >
    {channel}
  </Badge>
);

// Provisional-figure markers driven by the per-row flags (contract §6).
// `missing_rate` is the louder warning (total is undercounted); rendered first.
export const ProvisionalFlags = ({ pricingUnverified, missingRate }) => {
  if (!pricingUnverified && !missingRate) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {missingRate ? (
        <span
          title="Some usage isn't priced yet — total is a lower bound."
          className="inline-flex items-center gap-0.5 rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600"
        >
          <AlertTriangle className="h-3 w-3" /> undercounted
        </span>
      ) : null}
      {pricingUnverified ? (
        <span
          title="Includes estimated rates — pending confirmation."
          className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
        >
          <Info className="h-3 w-3" /> estimated
        </span>
      ) : null}
    </span>
  );
};
